<template>
  <div class="ds-page" v-if="!$store.getters.isForbidden">
    <!-- Page Header -->
    <div class="page-header d-print-none">
      <div class="row align-items-center">
        <div class="col">
          <div class="page-pretitle">Companies</div>
          <h2 class="page-title">Submission</h2>
          <p class="page-subtitle">Review and process company registration requests</p>
        </div>
        <div class="col-auto ms-auto">
          <button class="ds-btn ds-btn--secondary" @click="getRequestList">
            <i class="el-icon-refresh"></i>
            Refresh
          </button>
        </div>
      </div>
    </div>

    <!-- Filter Card -->
    <div class="ds-card">
      <div class="ds-card__body">
        <div class="sub-filter-bar">
          <div class="sub-filter-controls">
            <el-select size="small" v-model="search.search_by" style="width: 180px">
              <el-option v-for="(opt, i) in search_options" :key="i" :value="opt.value" :label="opt.text" />
            </el-select>
            <el-input
              clearable
              size="small"
              type="text"
              v-model="search.search_keyword"
              placeholder="Input search keyword..."
              style="width: 240px"
              @clear="searchHandler"
            />
          </div>
          <div class="sub-filter-actions">
            <button @click="resetFilter" class="ds-btn ds-btn--secondary ds-btn--sm">Reset</button>
            <button @click="searchHandler" class="ds-btn ds-btn--blue ds-btn--sm">
              <font-awesome-icon icon="search" />
              Search
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Table Card -->
    <div class="ds-card">
      <div class="ds-table-wrap" v-loading="isLoading">
        <table class="ds-table">
          <thead>
            <tr>
              <th>Created At</th>
              <th>Company</th>
              <th>Requestor</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, i) in list" :key="i">
              <td>
                <span class="ds-text-sm ds-text-muted">{{ dsFormatDateTime(item.created_at) }}</span>
              </td>
              <td>
                <span class="ds-text-bold">{{ item.data && item.data.company_name ? item.data.company_name : "—" }}</span>
              </td>
              <td>
                <div class="ds-user-cell">
                  <div class="ds-avatar" :class="dsAvatarVariant(item.user && item.user.full_name)">
                    {{ dsGetInitials(item.user && item.user.full_name) }}
                  </div>
                  <div>
                    <div class="ds-user-cell__name">{{ item.user && item.user.full_name ? item.user.full_name : "—" }}</div>
                    <div class="ds-user-cell__sub">{{ item.user && item.user.email ? item.user.email : "" }}</div>
                  </div>
                </div>
              </td>
              <td>
                <span class="ds-pill" :class="submissionStatusClass(item.status)">
                  {{ submissionStatusLabel(item.status) }}
                </span>
              </td>
              <td>
                <div class="submission-action-group">
                  <el-tooltip content="View Data" placement="top">
                    <button type="button" class="ds-action-btn" @click="viewDetailData(item)">
                      <font-awesome-icon icon="eye" />
                    </button>
                  </el-tooltip>
                  <el-tooltip content="Update" placement="top">
                    <button type="button" class="ds-action-btn" @click="viewDetail(item)">
                      <font-awesome-icon icon="edit" />
                    </button>
                  </el-tooltip>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <el-empty v-if="total_rows === 0" description="No data found" :image="emptyStateImage" class="ds-empty" />

      <Pagination :total_rows="total_rows" :page="page" :per_page="per_page" :list="list" @pageChange="pageChangeHandler" />
    </div>

    <!-- Update Status Modal -->
    <b-modal size="lg" v-model="showDetailRequest" :title="'Detail #' + selected_item.id" hide-footer @show="onModalShow">
      <b-form @submit.prevent="doUpdateStatus" @reset="closeModalRequest">
        <b-form-group label="Created at">
          <b-input v-model="selected_item.created_at" :disabled="true" type="text" />
        </b-form-group>
        <b-form-group label="User Name">
          <b-input v-model="selected_item.user.full_name" :disabled="true" type="text" />
        </b-form-group>
        <b-form-group label="Email">
          <b-input v-model="selected_item.user.email" :disabled="true" type="text" />
        </b-form-group>
        <b-form-group label="Phone Number">
          <b-input v-model="selected_item.user.phone_number" :disabled="true" type="text" />
        </b-form-group>
        <b-form-group label="Status">
          <b-input v-model="displayLastLogStatus" :disabled="true" type="text" />
        </b-form-group>
        <b-form-group label="Set Status">
          <el-select class="w-full" v-model="selected_item.status">
            <el-option v-for="(opt, index) in filteredStatusOptions" :key="index" :value="opt.value" :label="opt.label" />
          </el-select>
        </b-form-group>
        <b-form-group label="Reason" v-if="['rejected_by_company', 'rejected_by_bd', 'cancelled'].includes(selected_item.status)">
          <b-textarea v-model="selected_item.reason" placeholder="Input reason" />
        </b-form-group>
        <div class="d-flex justify-content-end gap-2 mt-3">
          <button type="button" class="ds-btn ds-btn--secondary ds-btn--sm" @click="closeModalRequest">Cancel</button>
          <button type="button" class="ds-btn ds-btn--primary ds-btn--sm" :disabled="isSubmitDisabled" @click="doUpdateStatus">
            Submit
          </button>
        </div>
      </b-form>
    </b-modal>

    <!-- View Data Modal -->
    <b-modal size="lg" v-model="showDetailRequestData" :title="'Detail #' + selected_item.id" hide-footer @show="onModalShow">
      <b-form @reset="closeModalRequestData">
        <b-form-group label="PIC Name">
          <b-input v-model="selected_item.data.pic_name" :disabled="true" type="text" />
        </b-form-group>
        <b-form-group label="Submitted at">
          <b-input v-model="selected_item.created_at" :disabled="true" type="text" />
        </b-form-group>
        <b-form-group label="PIC Email">
          <b-input v-model="selected_item.data.pic_email" :disabled="true" type="text" />
        </b-form-group>
        <b-form-group label="PIC Phone Number">
          <b-input v-model="selected_item.data.pic_phone_number" :disabled="true" type="text" />
        </b-form-group>
        <b-form-group label="Address">
          <b-input v-model="selected_item.data.address" :disabled="true" type="text" />
        </b-form-group>
        <div class="d-flex justify-content-end mt-3">
          <button type="button" class="ds-btn ds-btn--secondary ds-btn--sm" @click="closeModalRequestData">Close</button>
        </div>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import moment from "moment";
import emptyStateImage from "@/assets/images/empty-state.png";
import popupErrorMessages from "@/library/popup-error-messages";
import { GET_REQUEST_COMPANY, UPDATE_REQUEST_JOIN } from "@/store/modules/companies";
import { designSystemMixin } from "@/mixins/designSystemMixin";
import Pagination from "../components/Pagination.vue";

export default {
  name: "Submission",
  components: {
    Pagination,
  },
  mixins: [designSystemMixin],
  metaInfo: {
    title: "Submission",
  },
  data() {
    return {
      emptyStateImage,
      moment,
      showModalCreate: false,
      selected_item: {
        id: "",
        created_at: "",
        user: {
          full_name: "",
          email: "",
          phone_number: "",
        },
        status: "",
        data: {
          pic_name: "",
          pic_email: "",
          pic_phone_number: "",
          company_name: "",
          address: "",
        },
        reason: "",
      },
      showDetailRequest: false,
      showDetailRequestData: false,
      per_page: 20,
      total_rows: 0,
      page: 1,
      list: [],
      isLoading: true,
      user_id: null,
      status_filter: "",
      search: {
        search_by: "company_name",
        search_keyword: "",
      },
      search_options: [
        { value: "company_name", text: "Company Name" },
        { value: "pic_name", text: "PIC Name" },
        { value: "pic_email", text: "PIC Email" },
        { value: "pic_phone_number", text: "PIC Phone Number" },
      ],
      status_options: [
        { value: "waiting_verification", label: "Waiting Verification" },
        { value: "approved", label: "Approved" },
        { value: "rejected_by_company", label: "Rejected by Company" },
        { value: "rejected_by_bd", label: "Rejected by BD" },
        { value: "rejected_by_hr", label: "Rejected by HR" },
        { value: "cancelled", label: "Cancelled" },
      ],
    };
  },
  computed: {
    isSubmitDisabled() {
      const statusRequiresReason = ["rejected_by_company", "rejected_by_bd", "cancelled"].includes(this.selected_item.status);
      return statusRequiresReason && !this.selected_item.reason;
    },
    displayLastLogStatus() {
      const statusMap = {
        submitted: "Submitted",
        waiting_verification: "Waiting Verification",
        approved: "Approved",
        rejected_by_hr: "Rejected By HR",
        rejected_by_bd: "Rejected By BD",
        cancelled: "Cancelled",
      };
      return statusMap[this.lastLogStatus] || "";
    },
    lastLogStatus() {
      if (this.selected_item && this.selected_item.company_request_logs && this.selected_item.company_request_logs.length) {
        return this.selected_item.company_request_logs.slice().sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0]
          .status;
      }
      return null;
    },
    filteredStatusOptions() {
      if (this.lastLogStatus === "submitted") {
        return this.status_options.filter((opt) => opt.value === "waiting_verification");
      }
      if (this.lastLogStatus === "waiting_verification") {
        return this.status_options.filter((opt) =>
          ["approved", "rejected_by_company", "rejected_by_bd", "cancelled"].includes(opt.value)
        );
      }
      return [];
    },
  },
  async mounted() {
    const loader = this.$loading.show();
    await this.getRequestList();
    loader.hide();
  },
  async created() {
    this.user_id = localStorage.getItem("user_id");
  },
  methods: {
    pageChangeHandler(page) {
      this.page = page;
      this.getRequestList();
    },
    isFinalStatus(status) {
      return ["approved", "rejected_by_company", "rejected_by_hr", "rejected_by_bd", "cancelled", "accepted", "test"].includes(
        status
      );
    },
    async getRequestList() {
      this.isLoading = true;
      try {
        await this.$store.dispatch(GET_REQUEST_COMPANY, {
          page: this.page,
          per_page: this.per_page,
          type: "create",
          ...(this.search.search_keyword
            ? {
                search_by: this.search.search_by,
                search_keyword: this.search.search_keyword,
              }
            : {}),
        });
        const { rows, count } = this.$store.getters.request_join;
        this.list = rows;
        this.total_rows = count;
      } finally {
        this.isLoading = false;
      }
    },
    searchHandler() {
      this.page = 1;
      this.getRequestList();
    },
    resetFilter() {
      this.search.search_by = "company_name";
      this.search.search_keyword = "";
      this.page = 1;
      this.getRequestList();
    },
    viewDetail(item) {
      this.showDetailRequest = true;
      this.selected_item = item;
    },
    viewDetailData(item) {
      this.showDetailRequestData = true;
      this.selected_item = item;
    },
    onModalShow() {
      this.originalStatus = this.selected_item.status;
    },
    closeModalRequest() {
      this.selected_item.status = this.originalStatus;
      this.showDetailRequest = false;
    },
    closeModalRequestData() {
      this.selected_item.status = this.originalStatus;
      this.showDetailRequestData = false;
    },
    async doUpdateStatus() {
      this.$confirm(
        "You will update the status of this company request. Make sure you have input the data correctly. Continue?",
        "Confirmation",
        {
          type: "info",
          beforeClose: async (action, instance, done) => {
            if (action === "confirm") {
              done();
              const requestBody = {
                request_id: this.selected_item.id,
                status: this.selected_item.status,
                user_id: this.user_id,
                reason: this.selected_item.reason,
              };
              await this.$store
                .dispatch(UPDATE_REQUEST_JOIN, { data: requestBody })
                .then(() => {
                  this.$message({
                    title: "Success",
                    type: "success",
                    message: "Status updated",
                  });
                  this.showDetailRequest = false;
                  this.getRequestList();
                })
                .catch((err) => {
                  popupErrorMessages(err.response.data);
                });
              this.showDetailRequest = false;
              return;
            }
            done();
          },
        }
      ).catch(() => {});
    },
    submissionStatusClass(status) {
      const map = {
        submitted: "ds-pill--info",
        waiting_verification: "ds-pill--warning",
        approved: "ds-pill--success",
        rejected_by_company: "ds-pill--danger",
        rejected_by_bd: "ds-pill--danger",
        rejected_by_hr: "ds-pill--danger",
        cancelled: "ds-pill--danger",
      };
      return map[String(status).toLowerCase()] || "ds-pill--gray";
    },
    submissionStatusLabel(status) {
      const map = {
        submitted: "Submitted",
        waiting_verification: "Waiting Verification",
        approved: "Approved",
        rejected_by_company: "Rejected by Company",
        rejected_by_bd: "Rejected by BD",
        rejected_by_hr: "Rejected by HR",
        cancelled: "Cancelled",
      };
      return map[String(status).toLowerCase()] || status;
    },
  },
};
</script>

<style scoped>
/* ── Filter Bar (single line) ────────────────────────────── */
.sub-filter-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}
.sub-filter-controls {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.sub-filter-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

/* ── Action buttons ──────────────────────────────────────── */
.submission-action-group {
  display: flex;
  gap: 6px;
  align-items: center;
}

.ds-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid rgba(101, 109, 119, 0.2);
  border-radius: 6px;
  background: #fff;
  color: #6c757d;
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s, background 0.15s;
  padding: 0;
  font-size: 13px;
}

.ds-action-btn:hover {
  border-color: #3b7ddd;
  color: #3b7ddd;
  background: rgba(59, 125, 221, 0.06);
}
</style>
